import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  LibraryStatusChart,
  ProgressChart,
  RatingDistributionChart,
  StatisticsCard,
  StatsIcons,
  TopGenresChart,
  TypeDistributionChart,
} from '@/components/dashboard'
import {
  ContinueReadingCard,
  GenreBacklogCard,
  MissingDatesBanner,
  PreferredVsActualCard,
  ReadingGoalCard,
  ReadingPaceChart,
  ReadingStreakCard,
  ReadingTimeCard,
  StalledBooksCard,
  TopAuthorsCard,
} from '@/components/insights'
import { LoansCard } from '@/components/loans'
import { PreferredGenresModal } from '@/components/settings/PreferredGenresModal'
import { useAuth } from '@/contexts/auth'
import { useDashboardStatistics, useFavoriteGenre } from '@/hooks/dashboard'
import { useLibraryBooks } from '@/hooks/insights'
import { useFirstTimeGenreSetup } from '@/hooks/settings/useFirstTimeGenreSetup'
import { bookListUrl } from '@/lib/utils'

export function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const displayName = user?.user_metadata?.full_name || user?.email || 'User'

  // Use optimized dashboard statistics hook
  const { statistics, isLoading, error } = useDashboardStatistics()
  const { favoriteGenre } = useFavoriteGenre()
  const { data: libraryBooks = [] } = useLibraryBooks()
  const borrowedRead = libraryBooks.filter(
    (b) => b.ownership === 'borrowed' && b.status === 'read'
  ).length

  // First-time genre setup
  const { showSetupModal, handleSetupComplete } = useFirstTimeGenreSetup()

  // Handle error state
  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-error">{t('errors.general')}</p>
        <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-foreground">
            {t('dashboard.welcomeBack', { name: displayName.split(' ')[0] })}
          </h1>
          <p className="mt-2 text-muted-foreground">{t('dashboard.readingJourneyOverview')}</p>
        </div>
        <Link
          to="/dashboard/year-in-review"
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent sm:self-auto"
        >
          🎉 {t('insights.yearInReview.cta')}
        </Link>
      </div>

      <MissingDatesBanner books={libraryBooks} />

      {/* Reading now: what's open, the yearly goal and the streak */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <ContinueReadingCard books={libraryBooks} />
        <ReadingGoalCard books={libraryBooks} />
        <ReadingStreakCard books={libraryBooks} />
      </div>

      {/* Statistics Grid - Using optimized StatisticsCard components */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatisticsCard
          title={t('dashboard.stats.totalBooks')}
          value={statistics.totalBooks}
          subtitle={
            statistics.borrowedBooks > 0
              ? t('dashboard.stats.ownedPlusBorrowed', { count: statistics.borrowedBooks })
              : statistics.totalBooks === 0
                ? t('dashboard.stats.startAddingBooks')
                : t('dashboard.stats.percentOfCollection', { percent: 100 })
          }
          icon={StatsIcons.TotalBooks}
          href={bookListUrl({ status: 'all' })}
          isLoading={isLoading}
        />

        <StatisticsCard
          title={t('dashboard.stats.booksRead')}
          value={statistics.readTotal}
          subtitle={
            borrowedRead > 0
              ? t('dashboard.stats.readIncludesBorrowed', { count: borrowedRead })
              : statistics.totalBooks > 0
                ? t('dashboard.stats.percentOfCollection', { percent: statistics.readPercentage })
                : t('dashboard.stats.startAddingBooks')
          }
          icon={StatsIcons.BooksRead}
          href={bookListUrl({ status: 'read' })}
          isLoading={isLoading}
        />

        <StatisticsCard
          title={t('dashboard.stats.currentlyReading')}
          value={statistics.currentlyReading}
          subtitle={t('dashboard.stats.booksInProgress')}
          icon={StatsIcons.CurrentlyReading}
          href={bookListUrl({ status: 'reading' })}
          isLoading={isLoading}
        />

        <StatisticsCard
          title={t('dashboard.stats.wishlist')}
          value={statistics.wishlistBooks}
          subtitle={t('dashboard.stats.booksToReadLater')}
          icon={StatsIcons.Wishlist}
          href="/dashboard/wishlist"
          isLoading={isLoading}
        />
      </div>

      {/* Enhanced Statistics Grid - New metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatisticsCard
          title={t('dashboard.stats.readThisYear')}
          value={statistics.readThisYear}
          subtitle={`${new Date().getFullYear()}`}
          icon={StatsIcons.Calendar}
          href={bookListUrl({ status: 'read', year: new Date().getFullYear() })}
          isLoading={isLoading}
        />

        <StatisticsCard
          title={t('dashboard.stats.favoriteGenre')}
          value={favoriteGenre?.name || '-'}
          subtitle={
            favoriteGenre
              ? `${favoriteGenre.count} ${t('dashboard.stats.booksWithGenre')}`
              : t('dashboard.stats.noFavoriteGenre')
          }
          icon={StatsIcons.Genre}
          href={favoriteGenre ? bookListUrl({ status: 'all', genre: favoriteGenre.id }) : undefined}
          isLoading={isLoading}
          genreColor={favoriteGenre?.color}
        />

        <StatisticsCard
          title={t('books.type.physical')}
          value={statistics.physicalBooks}
          subtitle={
            statistics.totalBooks > 0
              ? `${statistics.physicalPercentage}% ${t('dashboard.stats.ofTotal')}`
              : t('dashboard.stats.startAddingBooks')
          }
          icon={StatsIcons.Physical}
          href={bookListUrl({ status: 'all' })}
          isLoading={isLoading}
        />

        <StatisticsCard
          title={t('books.type.digital')}
          value={statistics.digitalBooks}
          subtitle={
            statistics.totalBooks > 0
              ? `${statistics.digitalPercentage}% ${t('dashboard.stats.ofTotal')}`
              : t('dashboard.stats.startAddingBooks')
          }
          icon={StatsIcons.Digital}
          href={bookListUrl({ type: 'digital', status: 'all' })}
          isLoading={isLoading}
        />
      </div>

      {/* Charts Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          {t('dashboard.analytics.title')}
        </h2>

        {/* Primary Charts Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 mb-6">
          {/* Yearly Progress Chart */}
          <ProgressChart />

          {/* Type Distribution Chart */}
          <TypeDistributionChart />
        </div>

        {/* Advanced Charts Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-6">
          {/* Library Status Chart */}
          <LibraryStatusChart />

          {/* Top Genres Chart */}
          <TopGenresChart />

          {/* Rating Distribution Chart */}
          <RatingDistributionChart />
        </div>
      </div>

      {/* Insights computed from the whole library */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-foreground mb-4">{t('insights.title')}</h2>

        <div className="grid gap-6 md:grid-cols-2 mb-6">
          <ReadingPaceChart books={libraryBooks} />
          <PreferredVsActualCard books={libraryBooks} />
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-6">
          <ReadingTimeCard books={libraryBooks} />
          <TopAuthorsCard books={libraryBooks} />
          <GenreBacklogCard books={libraryBooks} />
          <StalledBooksCard books={libraryBooks} />
          <LoansCard books={libraryBooks} />
        </div>
      </div>

      {/* First-time Genre Setup Modal */}
      <PreferredGenresModal
        isOpen={showSetupModal}
        onClose={handleSetupComplete}
        showSuggestions={true}
      />
    </div>
  )
}
